import React, { useState, useEffect } from 'react';
import { 
    Dialog, 
    DialogActions, 
    DialogContent, 
    DialogTitle, 
    Button, 
    Box, 
    CircularProgress, 
    Typography,
    Alert
} from '@mui/material';

const SobrePreviewModal = ({ 
    open, 
    onClose, 
    selectedOrden, 
    onConfirm, 
    title = "Vista Previa del Sobre",
    confirmText = "Imprimir",
    cancelText = "Cancelar"
}) => {
    const [pdfUrl, setPdfUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const server = process.env.REACT_APP_API_ROUTE;

    useEffect(() => {
        if (open && selectedOrden) {
            generatePdfPreview();
        }
        
        // Cleanup PDF URL when modal closes
        return () => {
            if (pdfUrl) {
                window.URL.revokeObjectURL(pdfUrl);
                setPdfUrl(null);
            }
        };
    }, [open, selectedOrden]);

    const generatePdfPreview = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await fetch(`${server}/MgOrdenesMedicas/ImprimirSobre/${selectedOrden.idOrdenMedica}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                throw new Error(`Error al generar el PDF: ${response.status} ${response.statusText}`);
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            setPdfUrl(url);
        } catch (err) {
            console.error('Error generating PDF preview:', err);
            setError(err.message || 'Error al generar la vista previa del sobre');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (pdfUrl) {
            window.URL.revokeObjectURL(pdfUrl);
            setPdfUrl(null);
        }
        setError(null);
        onClose();
    };

    const handleConfirm = () => {
        onConfirm();
        handleClose();
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            fullScreen
            PaperProps={{
                style: {
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100vh',
                    maxHeight: '100vh',
                    margin: 0
                }
            }}
        >
            <DialogTitle>
                {title}
                <Typography variant="body2" color="textSecondary">
                    {selectedOrden ? `Orden N°: ${selectedOrden.abreviatura}${selectedOrden.idOrdenMedicaMes} - ${selectedOrden.nombreCompleto}` : ''}
                </Typography>
            </DialogTitle>
            
            <DialogContent style={{ 
                padding: 0, 
                display: 'flex', 
                flexDirection: 'column',
                flex: 1,
                overflow: 'hidden'
            }}>
                {loading && (
                    <Box 
                        display="flex" 
                        flexDirection="column" 
                        alignItems="center" 
                        justifyContent="center" 
                        flex={1}
                        minHeight="50vh"
                    >
                        <CircularProgress size={60} />
                        <Typography variant="body1" style={{ marginTop: 16 }}>
                            Generando vista previa del sobre...
                        </Typography>
                    </Box>
                )}
                
                {error && (
                    <Box 
                        p={3}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flex={1}
                        minHeight="50vh"
                    >
                        <Alert severity="error" style={{ maxWidth: '600px' }}>
                            {error}
                        </Alert>
                    </Box>
                )}
                
                {pdfUrl && !loading && !error && (
                    <Box style={{ 
                        flex: 1, 
                        height: '100%',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <iframe
                            src={pdfUrl}
                            style={{
                                width: '100%',
                                height: '100%',
                                border: 'none',
                                flex: 1
                            }}
                            title="Vista previa del sobre"
                        />
                    </Box>
                )}
            </DialogContent>
            
            <DialogActions style={{ padding: '16px 24px' }}>
                <Button 
                    onClick={handleClose} 
                    color="secondary"
                    variant="outlined"
                >
                    {cancelText}
                </Button>
                <Button 
                    onClick={handleConfirm} 
                    color="primary"
                    variant="contained"
                    disabled={loading || error || !pdfUrl}
                >
                    {confirmText}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SobrePreviewModal;
